import { timelineStages, type TimelineStage } from "@/mocks/home";
import SectionHead from "./SectionHead";

const TONE: Record<TimelineStage["tone"], { dot: string; tag: string; bar: string }> = {
  start: { dot: "bg-primary-400", tag: "text-primary-300", bar: "bg-primary-500" },
  rise: { dot: "bg-primary-500", tag: "text-primary-300", bar: "bg-primary-500" },
  doubt: { dot: "bg-secondary-400", tag: "text-secondary-300", bar: "bg-secondary-500" },
  respond: { dot: "bg-accent-400", tag: "text-accent-300", bar: "bg-accent-500" },
  turn: { dot: "bg-accent-500", tag: "text-accent-300", bar: "bg-accent-500" },
};

export default function ReversalSection() {
  return (
    <section id="reversal" className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
      <SectionHead
        eyebrow="反转复盘现场"
        title="同一件事，是怎么一步步『反转』的"
        desc="以「深夜连麦录音疑云」为示例，拆解信息从初传到转向的全过程。反转不是一步到位的，往往藏在某个被忽略的细节里。"
      />

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
        <div className="relative">
          <span className="absolute left-[22px] top-2 bottom-2 hidden w-px bg-background-300/60 sm:block" />
          <ol className="space-y-4">
            {timelineStages.map((st, i) => {
              const t = TONE[st.tone];
              return (
                <li key={i} className="relative flex gap-4 sm:gap-6">
                  <div className="z-10 flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-full border border-background-300/60 bg-background-100">
                    <i className={`ri-${st.tone === "turn" ? "refresh-line" : st.tone === "respond" ? "chat-check-line" : st.tone === "doubt" ? "search-eye-line" : "megaphone-line"} text-lg ${t.dot}`} />
                  </div>
                  <div className="min-w-0 flex-1 rounded-lg border border-background-200/70 bg-background-100/50 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-[11px] font-semibold ${t.tag}`}>{st.tag}</span>
                      <span className="h-3 w-px bg-background-300/60" />
                      <h3 className="font-heading text-sm font-bold text-foreground-950 md:text-base">{st.title}</h3>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-foreground-600 md:text-sm">{st.detail}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        <aside className="flex flex-col gap-4">
          <div className="rounded-lg border border-accent-500/30 bg-accent-500/10 p-5">
            <div className="flex items-center gap-2 text-accent-300">
              <i className="ri-lightbulb-flash-line text-lg" />
              <span className="font-heading text-sm font-bold">复盘一句话</span>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-foreground-800">
              这份反转的核心转折并不在「回应」，而在「质疑」那一步——当有人开始逐帧比对时间轴，情绪化的单方面说法才第一次被拉回事实。
            </p>
          </div>

          <div className="rounded-lg border border-background-200/70 bg-background-100/60 p-5">
            <div className="flex items-center gap-2 text-foreground-900">
              <i className="ri-list-check-3 text-lg text-secondary-400" />
              <span className="font-heading text-sm font-bold">看事件防被带偏的 3 步</span>
            </div>
            <ul className="mt-3 space-y-3">
              <li className="flex gap-2 text-xs text-foreground-600">
                <span className="mt-0.5 font-heading font-black text-secondary-400">01</span>
                先看来源是否可回溯，截图有没有采集时间与出处。
              </li>
              <li className="flex gap-2 text-xs text-foreground-600">
                <span className="mt-0.5 font-heading font-black text-secondary-400">02</span>
                把「网传」和「实锤」用词分开，警惕被情绪化导语带节奏。
              </li>
              <li className="flex gap-2 text-xs text-foreground-600">
                <span className="mt-0.5 font-heading font-black text-secondary-400">03</span>
                等当事人与平台给出可回溯的一手素材，再下结论。
              </li>
            </ul>
          </div>
        </aside>
      </div>
    </section>
  );
}